// Calls report (/analytics/calls). One API call (GET /analytics/calls/breakdown)
// returns pre-aggregated counts from the CallHourlyStats table for both call
// systems (rhinoVoip and RhinoCall), grouped finely enough that every filter and
// chart is derived in the browser. There is no record cap: the previous page
// read the first 1,000 CDRs and silently truncated busy ranges.
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import {
  Button, DateRangeDropdown, Dropdown, DropdownMenuItem, Icon, UtilityInlineGrid,
} from 'rhinostyle';
import PageLoader from './PageLoader';
import AskReggieButton from './AskReggieButton';
import {
  ChartCard, DonutChart, HorizontalBars, StackedBars, MultiLine, SortableTable, GroupingSelector, EmptyChart, csvColumns, OTHER_LABEL, OTHER_COLOR,
} from './MessagingReportsCharts';
import { useGetCallBreakdownQuery } from '../services/AnalyticsService';
import {
  REPORT_DATE_PICKER_LABELS, REPORT_TIMEZONE_OPTIONS, REPORT_DIRECTION_COLORS, REPORT_PIE_COLORS,
  categoryColor, readStoredTimezone, storeTimezone,
} from '../helpers/ReportsHelpers';
import {
  CALL_SOURCES, sourceLabel, memberKeyOf, channelKeyOf, filterRows, sumRows, finishTotals, formatHandleTime, daysInRange, summaryByPeriod,
  inboundByOutcome, byDirection, bySource, perHourSeries, perWeekdaySeries, perDaySeries, totalsByCategory, categoryByPeriod,
  dailyByCategory, OTHER_KEY,
} from '../helpers/CallReportsHelpers';
import { clearAskReggieContext, setAskReggieContext } from '../reducers/rhinoanalystReducer';

const TZ_STORAGE_KEY = 'voipReportsTimezone';
const MAX_RANGE_MONTHS = 12;

const STATUS_COLORS = {
  Answered: '#5DBE85', // deeper mint — positive
  Voicemail: '#F37846', // orange — voicemail convention
  Missed: '#A60B4F', // deep magenta — strongest negative
  'No Answer': '#FFC274', // light gold — warning warm
  Busy: '#4FBFA8', // teal
  Failed: '#E94B4F', // coral — error variant, distinct from Missed
};
const statusColor = (name, index) => STATUS_COLORS[name] || categoryColor(index);
const directionColor = (name) => REPORT_DIRECTION_COLORS[name] || REPORT_PIE_COLORS[0];
const indexColor = (name, index) => categoryColor(index);

const TAB_LABELS = {
  summary: 'Summary', members: 'Members', channels: 'Channels', ringGroups: 'Ring Groups',
};

const DIRECTION_BARS = [
  { key: 'inbound', name: 'Inbound', color: REPORT_DIRECTION_COLORS.Inbound },
  { key: 'outbound', name: 'Outbound', color: REPORT_DIRECTION_COLORS.Outbound },
];
const ANSWERED_MISSED_BARS = [
  { key: 'answered', name: 'Answered', color: STATUS_COLORS.Answered },
  { key: 'missed', name: 'Missed', color: STATUS_COLORS.Missed },
];
const CALLS_BAR = [{ key: 'total', name: 'Calls', color: REPORT_DIRECTION_COLORS.Outbound }];
const RHINOCALL_OUTCOME_BARS = [
  { key: 'answeredAll', name: 'Answered', color: STATUS_COLORS.Answered },
  { key: 'voicemailAll', name: 'Voicemail', color: STATUS_COLORS.Voicemail },
  { key: 'noAnswer', name: 'No Answer', color: STATUS_COLORS['No Answer'] },
];

const pieOf = (rows, nameKey = 'name', valueKey = 'total') => rows.filter((r) => r[valueKey] > 0).map((r) => ({ name: r[nameKey], value: r[valueKey] }));
const pct = (v) => `${v}%`;

const RhinoVOIPReports = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('summary');
  const [viewMode, setViewMode] = useState('graphs');
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [activeKey, setActiveKey] = useState(3);
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRingGroup, setSelectedRingGroup] = useState(null);
  const [tzPref, setTzPref] = useState(() => readStoredTimezone(TZ_STORAGE_KEY));
  const [summaryGrouping, setSummaryGrouping] = useState('total');
  const [memberGrouping, setMemberGrouping] = useState('total');
  const [channelGrouping, setChannelGrouping] = useState('total');
  const [rgGrouping, setRgGrouping] = useState('total');

  useEffect(() => { storeTimezone(TZ_STORAGE_KEY, tzPref); }, [tzPref]);

  // 'auto' resolves to the browser zone each render so travel is reflected.
  const tz = useMemo(() => (tzPref === 'auto' ? moment.tz.guess() : tzPref), [tzPref]);
  const tzLabel = useMemo(() => {
    const opt = REPORT_TIMEZONE_OPTIONS.find((o) => o.id === tzPref);
    if (!opt) return 'My Current Timezone';
    return tzPref === 'auto' ? `${opt.label} (${moment.tz(tz).format('z')})` : opt.label;
  }, [tzPref, tz]);

  const from = startDate.format('YYYY-MM-DD');
  const to = endDate.format('YYYY-MM-DD');
  const pickedStart = useMemo(() => moment.tz(from, tz).startOf('day'), [from, tz]);
  const pickedEnd = useMemo(() => moment.tz(to, tz).startOf('day'), [to, tz]);
  const days = useMemo(() => daysInRange(pickedStart, pickedEnd), [pickedStart, pickedEnd]);

  const { data: breakdown, isLoading, isFetching } = useGetCallBreakdownQuery({ from, to, tz });
  const members = useMemo(() => breakdown?.members || [], [breakdown]);
  const channels = useMemo(() => breakdown?.channels || [], [breakdown]);
  const allRows = useMemo(() => breakdown?.daily || [], [breakdown]);

  // Member options: rhinoapi members seen in the range (either call system) plus
  // phone extensions that are not assigned to a member, so their calls are
  // still reachable from the filter.
  const memberOptions = useMemo(() => {
    const options = members.map((m) => ({ key: `m${m.id}`, name: m.name }));
    const seen = new Set(options.map((o) => o.key));
    allRows.forEach((row) => {
      const key = memberKeyOf(row);
      if (key && !seen.has(key)) { seen.add(key); options.push({ key, name: `Ext ${row.extension}` }); }
    });
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [members, allRows]);
  const memberName = useCallback((key) => memberOptions.find((o) => o.key === key)?.name || key, [memberOptions]);
  const ringGroupOptions = useMemo(() => (breakdown?.ringGroups || []).filter(Boolean).sort(), [breakdown]);
  const sourcesPresent = useMemo(() => breakdown?.sources || [], [breakdown]);
  const showSourceFilter = sourcesPresent.length > 1;
  const hasRhinocall = sourcesPresent.includes('rhinocall');
  // RhinoCall view: every call is outbound and placed from a channel, so the
  // inbound/outbound and ring group views do not apply; channels do.
  const rhinocallMode = selectedSource === 'rhinocall' || (sourcesPresent.length === 1 && hasRhinocall);
  const channelName = useCallback((id) => {
    const c = channels.find((ch) => ch.id === id);
    if (!c) return `Channel ${id}`;
    return c.deleted ? `${c.name} (deleted)` : c.name;
  }, [channels]);
  const tabs = useMemo(() => {
    const ids = ['summary', 'members'];
    if (hasRhinocall) ids.push('channels');
    if (!rhinocallMode && ringGroupOptions.length > 0) ids.push('ringGroups');
    return ids.map((id) => ({ id, label: TAB_LABELS[id] }));
  }, [hasRhinocall, rhinocallMode, ringGroupOptions.length]);
  useEffect(() => {
    if (!tabs.some((t) => t.id === activeTab)) setActiveTab('summary');
  }, [tabs, activeTab]);

  const filters = useMemo(
    () => ({ source: selectedSource, memberKey: selectedMember, ringGroup: selectedRingGroup }),
    [selectedSource, selectedMember, selectedRingGroup],
  );
  const daily = useMemo(() => filterRows(allRows, filters), [allRows, filters]);
  const byHour = useMemo(() => filterRows(breakdown?.byHour || [], filters), [breakdown, filters]);
  const byWeekday = useMemo(() => filterRows(breakdown?.byWeekday || [], filters), [breakdown, filters]);
  const hasData = daily.length > 0;

  // --- Summary ---
  const totals = useMemo(() => finishTotals(sumRows(daily), days.length), [daily, days.length]);
  const summaryRows = useMemo(() => summaryByPeriod(daily, days, summaryGrouping), [daily, days, summaryGrouping]);
  const statusPie = useMemo(() => inboundByOutcome(daily, rhinocallMode), [daily, rhinocallMode]);
  const directionPie = useMemo(() => byDirection(daily), [daily]);
  const sourcePie = useMemo(() => bySource(daily), [daily]);
  const hourSeries = useMemo(() => perHourSeries(byHour, days.length), [byHour, days.length]);
  const weekdaySeries = useMemo(() => perWeekdaySeries(byWeekday, days), [byWeekday, days]);
  const daySeries = useMemo(() => perDaySeries(daily, days), [daily, days]);

  // --- Members: a call belongs to the member who answered it (inbound) or
  // placed it (outbound). Unanswered inbound calls have no member. ---
  const memberRows = useMemo(() => daily.filter((r) => memberKeyOf(r) !== null), [daily]);
  const memberSeed = useMemo(() => (selectedMember ? memberOptions.filter((o) => o.key === selectedMember) : memberOptions), [memberOptions, selectedMember]);
  const memberTotals = useMemo(() => totalsByCategory(memberRows, memberKeyOf, memberName, days.length, memberSeed), [memberRows, memberName, days.length, memberSeed]);
  const memberTable = useMemo(
    () => categoryByPeriod(memberRows, memberKeyOf, memberName, days, memberGrouping, memberSeed),
    [memberRows, memberName, days, memberGrouping, memberSeed],
  );
  const memberDaily = useMemo(() => dailyByCategory(memberRows, memberKeyOf, days), [memberRows, days]);

  // --- Channels: RhinoCall calls only (rhinoVoip calls have no channel). ---
  const channelRows = useMemo(() => daily.filter((r) => r.source === 'rhinocall' && r.channelId), [daily]);
  const channelTotals = useMemo(() => totalsByCategory(channelRows, channelKeyOf, channelName, days.length), [channelRows, channelName, days.length]);
  const channelTable = useMemo(
    () => categoryByPeriod(channelRows, channelKeyOf, channelName, days, channelGrouping),
    [channelRows, channelName, days, channelGrouping],
  );
  const channelDaily = useMemo(() => dailyByCategory(channelRows, channelKeyOf, days), [channelRows, days]);

  // --- Ring groups: rhinoVoip inbound calls only (RhinoCall has no ring groups). ---
  const rgKeyOf = (r) => r.ringGroup || null;
  const rgRows = useMemo(() => daily.filter((r) => r.source === 'rhinovoip' && r.direction === 'inbound' && r.ringGroup), [daily]);
  const rgSeed = useMemo(() => (selectedRingGroup ? [selectedRingGroup] : ringGroupOptions).map((name) => ({ key: name, name })), [ringGroupOptions, selectedRingGroup]);
  const rgTotals = useMemo(() => totalsByCategory(rgRows, rgKeyOf, (k) => k, days.length, rgSeed), [rgRows, days.length, rgSeed]);
  const rgTable = useMemo(() => categoryByPeriod(rgRows, rgKeyOf, (k) => k, days, rgGrouping, rgSeed), [rgRows, days, rgGrouping, rgSeed]);
  const rgDaily = useMemo(() => dailyByCategory(rgRows, rgKeyOf, days), [rgRows, days]);

  // --- Filters / dates ---
  const sourceFilterLabel = sourceLabel(selectedSource);
  const memberLabel = selectedMember === null ? 'All Members' : memberName(selectedMember);
  const ringGroupLabel = selectedRingGroup === null ? 'All Ring Groups' : selectedRingGroup;
  const isAnyFilterSelected = selectedSource !== 'all' || selectedMember !== null || selectedRingGroup !== null;
  const clearAllFilters = () => { setSelectedSource('all'); setSelectedMember(null); setSelectedRingGroup(null); };

  const selectDate = (date) => {
    if (date && date.startDate && date.endDate) {
      const start = moment(date.startDate);
      const end = moment(date.endDate);
      if (end.diff(start, 'months', true) > MAX_RANGE_MONTHS) {
        start.set(end.clone().subtract(MAX_RANGE_MONTHS, 'months').toObject());
      }
      setStartDate(start);
      setEndDate(end);
      setActiveKey(date.activeKey);
    }
  };

  const calculateDateRange = (duration, selectedLabelIndex) => {
    let fromDate = moment().subtract(duration, 'days');
    let toDate = moment();
    if (selectedLabelIndex === 1) toDate = moment().subtract(1, 'days');
    if (duration === 'year') fromDate = moment().subtract(1, 'years').add(1, 'days');
    return { startDate: fromDate, endDate: toDate };
  };

  const csvFilename = (title) => `${title}_${from}-${to}.csv`;

  // Publish what is on screen to the Ask Reggie drawer (global to the Analytics
  // section) so its answers follow the date range and filters chosen here.
  useEffect(() => {
    const rangeText = days.length === 30 ? 'Last 30 days' : `${pickedStart.format('MMM D')} - ${pickedEnd.format('MMM D, YYYY')}`;
    const contextFilters = { tab: activeTab, timezone: tz };
    if (selectedSource !== 'all') contextFilters['call system'] = sourceFilterLabel;
    if (selectedMember !== null) contextFilters.member = memberLabel;
    if (selectedRingGroup !== null) contextFilters['ring group'] = selectedRingGroup;
    dispatch(setAskReggieContext({
      page: 'Calls (VoIP Reports)',
      label: `Calls • ${rangeText}`,
      filters: contextFilters,
      dateRange: { from, to },
    }));
  }, [dispatch, days.length, pickedStart, pickedEnd, activeTab, tz, selectedSource, sourceFilterLabel, selectedMember, memberLabel, selectedRingGroup, from, to]);

  useEffect(() => () => { dispatch(clearAskReggieContext()); }, [dispatch]);

  const chart = (height, node) => (hasData ? node : <EmptyChart height={height} />);
  const summaryColumns = rhinocallMode ? [
    { key: 'period', label: 'Period' },
    { key: 'total', label: 'Total Calls' },
    { key: 'avgPerDay', label: 'Avg. Calls/Day' },
    { key: 'answeredAll', label: '# Answered' },
    { key: 'voicemailAll', label: '# Voicemail' },
    { key: 'noAnswer', label: '# No Answer' },
    { key: 'pctAnswered', label: '% Answered', format: (r) => pct(r.pctAnswered) },
    { key: 'avgHandleTime', label: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
  ] : [
    { key: 'period', label: 'Period' },
    { key: 'total', label: 'Total Calls' },
    { key: 'avgPerDay', label: 'Avg. Calls/Day' },
    { key: 'inbound', label: '# Inbound' },
    { key: 'outbound', label: '# Outbound' },
    { key: 'missed', label: '# Missed' },
    { key: 'voicemail', label: '# Voicemail' },
    { key: 'failed', label: '# Failed' },
    { key: 'pctMissed', label: '% Missed', format: (r) => pct(r.pctMissed) },
    { key: 'avgHandleTime', label: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
  ];
  const categoryColumns = (label, grouping, { inboundOnly = false } = {}) => (rhinocallMode ? [
    { key: 'name', label },
    ...(grouping !== 'total' ? [{ key: 'period', label: 'Period' }] : []),
    { key: 'total', label: 'Total Calls' },
    { key: 'avgPerDay', label: 'Avg. Calls/Day' },
    { key: 'answeredAll', label: '# Answered' },
    { key: 'voicemailAll', label: '# Voicemail' },
    { key: 'noAnswer', label: '# No Answer' },
    { key: 'pctAnswered', label: '% Answered', format: (r) => pct(r.pctAnswered) },
    { key: 'avgHandleTime', label: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
  ] : [
    { key: 'name', label },
    ...(label === 'Member' ? [{ key: 'extension', label: 'Extension' }] : []),
    ...(grouping !== 'total' ? [{ key: 'period', label: 'Period' }] : []),
    { key: 'total', label: 'Total Calls' },
    { key: 'avgPerDay', label: 'Avg. Calls/Day' },
    ...(inboundOnly ? [] : [{ key: 'inbound', label: '# Inbound' }, { key: 'outbound', label: '# Outbound' }]),
    { key: 'answered', label: '# Answered' },
    { key: 'missed', label: '# Missed' },
    { key: 'voicemail', label: '# Voicemail' },
    { key: 'pctMissed', label: '% Missed', format: (r) => pct(r.pctMissed) },
    { key: 'avgHandleTime', label: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
  ]);

  const renderFilters = () => (
    <div className="cdr-records__filter-wrapper">
      <div className="cdr-records__filter-label">
        FILTER BY:
        {isAnyFilterSelected && (
          <Button type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
        )}
      </div>
      <div className="row">
        <div>
          <UtilityInlineGrid>
            <DateRangeDropdown
              name="Reports Date Picker"
              startDate={startDate}
              endDate={endDate}
              selectDate={selectDate}
              minDate={moment().subtract(1, 'year')}
              maxDate={moment()}
              activeKey={activeKey}
              position="center"
              dropdownMenuItems={REPORT_DATE_PICKER_LABELS}
              selectDateRange={calculateDateRange}
              dataCypress="reportsDatePicker"
              isCustomDate
            />
            {showSourceFilter && (
              <Dropdown
                position="bottom"
                activeKey={selectedSource}
                label={sourceFilterLabel}
                type={selectedSource !== 'all' ? 'primary' : 'input'}
                outlined={selectedSource !== 'all'}
                name="reportsSource"
                onSelect={(id) => setSelectedSource(id)}
                dataCypress="reportsSource"
              >
                {CALL_SOURCES.map((s) => (
                  <DropdownMenuItem key={s.id} id={s.id} label={s.label} icon={selectedSource === s.id ? 'checkmark' : ''} />
                ))}
              </Dropdown>
            )}
            <Dropdown
              position="bottom"
              activeKey={selectedMember || ''}
              label={memberLabel}
              type={selectedMember ? 'primary' : 'input'}
              outlined={!!selectedMember}
              name="reportsMember"
              onSelect={(key) => setSelectedMember(key === 'all' || key === selectedMember ? null : key)}
              dataCypress="reportsMember"
            >
              <DropdownMenuItem id="all" label="All Members" icon={!selectedMember ? 'checkmark' : ''} />
              {memberOptions.map((m) => (
                <DropdownMenuItem key={m.key} id={m.key} label={m.name} icon={selectedMember === m.key ? 'checkmark' : ''} />
              ))}
            </Dropdown>
            {!rhinocallMode && ringGroupOptions.length > 0 && (
              <Dropdown
                position="bottom"
                activeKey={selectedRingGroup || ''}
                label={ringGroupLabel}
                type={selectedRingGroup ? 'primary' : 'input'}
                outlined={!!selectedRingGroup}
                name="reportsRingGroup"
                onSelect={(rg) => setSelectedRingGroup(rg === 'all' || rg === selectedRingGroup ? null : rg)}
                dataCypress="reportsRingGroup"
              >
                <DropdownMenuItem id="all" label="All Ring Groups" icon={!selectedRingGroup ? 'checkmark' : ''} />
                {ringGroupOptions.map((rg) => (
                  <DropdownMenuItem key={rg} id={rg} label={rg} icon={selectedRingGroup === rg ? 'checkmark' : ''} />
                ))}
              </Dropdown>
            )}
            <Dropdown
              position="bottom"
              activeKey={tzPref}
              label={tzLabel}
              type={tzPref !== 'auto' ? 'primary' : 'input'}
              outlined={tzPref !== 'auto'}
              name="reportsTimezone"
              onSelect={(id) => setTzPref(id)}
              dataCypress="reportsTimezone"
            >
              {REPORT_TIMEZONE_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.id} id={opt.id} label={opt.label} icon={tzPref === opt.id ? 'checkmark' : ''} />
              ))}
            </Dropdown>
          </UtilityInlineGrid>
        </div>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="voip-reports__tabs">
      <div className="voip-reports__tabs-left">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`voip-reports__tab${activeTab === tab.id ? ' voip-reports__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="voip-reports__view-toggle">
        {['graphs', 'table'].map((mode) => (
          <button
            key={mode}
            type="button"
            className={`voip-reports__view-btn${viewMode === mode ? ' voip-reports__view-btn--active' : ''}`}
            onClick={() => setViewMode(mode)}
          >
            {mode === 'graphs' ? 'Graphs' : 'Table'}
          </button>
        ))}
      </div>
    </div>
  );

  const renderSummary = () => (
    <div className="voip-reports__charts">
      {viewMode === 'table' && (
        <ChartCard
          title="Call Summary"
          csv={{ columns: csvColumns(summaryColumns), rows: summaryRows, filename: csvFilename('Call Summary') }}
          actions={<GroupingSelector value={summaryGrouping} onChange={setSummaryGrouping} />}
        >
          <SortableTable columns={summaryColumns} rows={summaryRows} defaultSort="period" defaultAsc rowKey={(r) => r.period} />
        </ChartCard>
      )}
      {viewMode === 'graphs' && rhinocallMode && (
        <>
          <div className="voip-reports__pie-row">
            <ChartCard half title={`Calls by Status (${totals.total} total, ${totals.pctAnswered}% answered)`} csv={{ columns: [{ key: 'name', header: 'Status' }, { key: 'value', header: 'Calls' }], rows: statusPie, filename: csvFilename('Calls by Status') }}>
              <DonutChart data={statusPie} colorFor={statusColor} valueLabel="Calls" />
            </ChartCard>
            <ChartCard half title={`Average Handle Time (${formatHandleTime(totals.avgHandleTime)}, answered in ${totals.avgAnswerSecs}s on average)`}>
              <HorizontalBars
                data={[{ name: 'Talk time (sec)', value: totals.avgHandleTime }, { name: 'Time to answer (sec)', value: totals.avgAnswerSecs }]}
                bars={[{ key: 'value', name: 'Seconds' }]}
              />
            </ChartCard>
          </div>
          <ChartCard title="Average Calls Per Hour" csv={{ columns: [{ key: 'hour', header: 'Hour' }, { key: 'total', header: 'Calls' }], rows: hourSeries, filename: csvFilename('Average Calls Per Hour') }}>
            {chart(300, <StackedBars data={hourSeries} xKey="hour" bars={CALLS_BAR} stackId="hr" interval={1} />)}
          </ChartCard>
          <ChartCard title="Average Calls by Day of Week" csv={{ columns: [{ key: 'day', header: 'Day' }, { key: 'total', header: 'Calls' }], rows: weekdaySeries, filename: csvFilename('Average Calls by Day of Week') }}>
            {chart(300, <StackedBars data={weekdaySeries} xKey="day" bars={CALLS_BAR} stackId="dow" interval={0} />)}
          </ChartCard>
          <ChartCard title="Calls Per Day" csv={{ columns: [{ key: 'day', header: 'Date' }, { key: 'total', header: 'Calls' }], rows: daySeries, filename: csvFilename('Calls Per Day') }}>
            {chart(300, <StackedBars data={daySeries} xKey="date" bars={CALLS_BAR} stackId="day" />)}
          </ChartCard>
        </>
      )}
      {viewMode === 'graphs' && !rhinocallMode && (
        <>
          <div className="voip-reports__pie-row">
            <ChartCard half title={`Inbound Calls by Status (${totals.inbound} inbound, ${totals.pctMissed}% missed)`} csv={{ columns: [{ key: 'name', header: 'Status' }, { key: 'value', header: 'Calls' }], rows: statusPie, filename: csvFilename('Inbound Calls by Status') }}>
              <DonutChart data={statusPie} colorFor={statusColor} valueLabel="Calls" />
            </ChartCard>
            <ChartCard half title={`Calls by Direction (${totals.total} total, ${totals.avgPerDay}/day)`} csv={{ columns: [{ key: 'name', header: 'Direction' }, { key: 'value', header: 'Calls' }], rows: directionPie, filename: csvFilename('Calls by Direction') }}>
              <DonutChart data={directionPie} colorFor={directionColor} valueLabel="Calls" />
            </ChartCard>
          </div>
          {showSourceFilter && (
            <div className="voip-reports__pie-row">
              <ChartCard half title="Calls by Call System" csv={{ columns: [{ key: 'name', header: 'Call System' }, { key: 'value', header: 'Calls' }], rows: sourcePie, filename: csvFilename('Calls by Call System') }}>
                <DonutChart data={sourcePie} colorFor={indexColor} valueLabel="Calls" />
              </ChartCard>
              <ChartCard half title={`Average Handle Time (${formatHandleTime(totals.avgHandleTime)}, answered in ${totals.avgAnswerSecs}s on average)`}>
                <HorizontalBars
                  data={[{ name: 'Talk time (sec)', value: totals.avgHandleTime }, { name: 'Time to answer (sec)', value: totals.avgAnswerSecs }]}
                  bars={[{ key: 'value', name: 'Seconds' }]}
                />
              </ChartCard>
            </div>
          )}
          <ChartCard title="Average Calls Per Hour" csv={{ columns: [{ key: 'hour', header: 'Hour' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], rows: hourSeries, filename: csvFilename('Average Calls Per Hour') }}>
            {chart(300, <StackedBars data={hourSeries} xKey="hour" bars={DIRECTION_BARS} stackId="hr" interval={1} />)}
          </ChartCard>
          <ChartCard title="Average Calls by Day of Week" csv={{ columns: [{ key: 'day', header: 'Day' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], rows: weekdaySeries, filename: csvFilename('Average Calls by Day of Week') }}>
            {chart(300, <StackedBars data={weekdaySeries} xKey="day" bars={DIRECTION_BARS} stackId="dow" interval={0} />)}
          </ChartCard>
          <ChartCard title="Incoming vs Outgoing Calls Per Day" csv={{ columns: [{ key: 'day', header: 'Date' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], rows: daySeries, filename: csvFilename('Calls Per Day') }}>
            {chart(300, <StackedBars data={daySeries} xKey="date" bars={DIRECTION_BARS} stackId="day" />)}
          </ChartCard>
        </>
      )}
    </div>
  );

  const renderCategoryTab = ({ label, totalsRows, tableRows, dailySeries, grouping, setGrouping, nameOf, inboundOnly }) => {
    const columns = categoryColumns(label, grouping, { inboundOnly });
    const missedRows = totalsRows.filter((r) => r.inbound > 0);
    const activeRows = totalsRows.filter((r) => r.total > 0);
    return (
      <div className="voip-reports__charts">
        {viewMode === 'table' && (
          <ChartCard
            title={`${label} Breakdown`}
            csv={{ columns: csvColumns(columns), rows: tableRows, filename: csvFilename(`${label} Breakdown`) }}
            actions={<GroupingSelector value={grouping} onChange={setGrouping} />}
          >
            <SortableTable columns={columns} rows={tableRows} defaultSort={grouping === 'total' ? 'total' : 'name'} defaultAsc={grouping !== 'total'} rowKey={(r) => `${r.key}-${r.period || ''}`} />
          </ChartCard>
        )}
        {viewMode === 'graphs' && (
          <>
            <div className="voip-reports__pie-row">
              <ChartCard half title={`Total Call Distribution Per ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'total', header: 'Calls' }], rows: totalsRows, filename: csvFilename(`Total Call Distribution Per ${label}`) }}>
                <DonutChart data={pieOf(totalsRows)} colorFor={indexColor} valueLabel="Calls" />
              </ChartCard>
              {rhinocallMode ? (
                <ChartCard half title={`Answered % by ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'pctAnswered', header: '% Answered', format: (r) => pct(r.pctAnswered) }], rows: activeRows, filename: csvFilename(`Answered Pct by ${label}`) }}>
                  <HorizontalBars data={activeRows} bars={[{ key: 'pctAnswered', name: '% Answered', color: STATUS_COLORS.Answered }]} formatter={pct} domain={[0, 100]} />
                </ChartCard>
              ) : (
                <ChartCard half title={`Missed Call % by ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'pctMissed', header: '% Missed', format: (r) => pct(r.pctMissed) }], rows: missedRows, filename: csvFilename(`Missed Call Pct by ${label}`) }}>
                  <HorizontalBars data={missedRows} bars={[{ key: 'pctMissed', name: '% Missed', color: STATUS_COLORS.Missed }]} formatter={pct} domain={[0, 100]} />
                </ChartCard>
              )}
            </div>
            <ChartCard title={`Total Calls Per ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'total', header: 'Calls' }], rows: totalsRows, filename: csvFilename(`Total Calls Per ${label}`) }}>
              <HorizontalBars data={totalsRows.filter((r) => r.total > 0)} bars={[{ key: 'total', name: 'Calls' }]} />
            </ChartCard>
            <ChartCard
              title={`Daily Calls Per ${label}`}
              csv={{ columns: [{ key: 'day', header: 'Date' }, ...dailySeries.keys.map((k) => ({ key: k, header: k === OTHER_KEY ? OTHER_LABEL : nameOf(k) }))], rows: dailySeries.data, filename: csvFilename(`Daily Calls Per ${label}`) }}
            >
              <MultiLine
                data={dailySeries.data}
                series={dailySeries.keys.map((k) => ({ key: k, name: k === OTHER_KEY ? OTHER_LABEL : nameOf(k), color: k === OTHER_KEY ? OTHER_COLOR : undefined }))}
              />
            </ChartCard>
            <ChartCard title={`Average Calls Per Day by ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'avgPerDay', header: 'Avg. Calls/Day' }], rows: totalsRows, filename: csvFilename(`Average Calls Per Day by ${label}`) }}>
              <HorizontalBars data={totalsRows.filter((r) => r.total > 0)} bars={[{ key: 'avgPerDay', name: 'Avg. Calls/Day' }]} />
            </ChartCard>
            {rhinocallMode && (
              <ChartCard title={`Calls by Status Per ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'answeredAll', header: 'Answered' }, { key: 'voicemailAll', header: 'Voicemail' }, { key: 'noAnswer', header: 'No Answer' }], rows: activeRows, filename: csvFilename(`Calls by Status Per ${label}`) }}>
                <HorizontalBars data={activeRows} bars={RHINOCALL_OUTCOME_BARS} />
              </ChartCard>
            )}
            {!rhinocallMode && inboundOnly && (
              <ChartCard title={`Answered vs Missed Per ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'answered', header: 'Answered' }, { key: 'missed', header: 'Missed' }], rows: totalsRows, filename: csvFilename(`Answered vs Missed Per ${label}`) }}>
                <HorizontalBars data={totalsRows.filter((r) => r.total > 0)} bars={ANSWERED_MISSED_BARS} />
              </ChartCard>
            )}
            {!rhinocallMode && !inboundOnly && (
              <ChartCard title={`Inbound vs Outbound Per ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], rows: totalsRows, filename: csvFilename(`Inbound vs Outbound Per ${label}`) }}>
                <HorizontalBars data={totalsRows.filter((r) => r.total > 0)} bars={DIRECTION_BARS} />
              </ChartCard>
            )}
          </>
        )}
      </div>
    );
  };

  const renderActiveTab = () => {
    if (isLoading || isFetching) return <PageLoader />;
    if (activeTab === 'members') {
      return renderCategoryTab({
        label: 'Member', totalsRows: memberTotals, tableRows: memberTable, dailySeries: memberDaily, grouping: memberGrouping, setGrouping: setMemberGrouping, nameOf: memberName, inboundOnly: false,
      });
    }
    if (activeTab === 'channels') {
      return renderCategoryTab({
        label: 'Channel', totalsRows: channelTotals, tableRows: channelTable, dailySeries: channelDaily, grouping: channelGrouping, setGrouping: setChannelGrouping, nameOf: (k) => channelName(Number(k)), inboundOnly: false,
      });
    }
    if (activeTab === 'ringGroups') {
      return renderCategoryTab({
        label: 'Ring Group', totalsRows: rgTotals, tableRows: rgTable, dailySeries: rgDaily, grouping: rgGrouping, setGrouping: setRgGrouping, nameOf: (k) => k, inboundOnly: true,
      });
    }
    return renderSummary();
  };

  return (
    <div className="app-page__container">
      <div className="app-panels">
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="app-page__header u-flex-direction-column">
              <div className="app-page__header__title cdr-records__title rhinoanalyst-title-row">
                Calls
                <AskReggieButton />
              </div>
            </div>
            <div className="box responsive-table cdr-records__grid">
              {renderFilters()}
              {renderTabs()}
              {activeTab === 'members' && (
                <p className="u-text-muted u-text-small">
                  A call is credited to the member who answered it (inbound) or placed it (outbound). Inbound calls nobody answered are not attributed to a member.
                </p>
              )}
              {activeTab === 'channels' && (
                <p className="u-text-muted u-text-small">
                  Channels apply to RhinoCall calls, which are placed from a channel. rhinoVoip phone system calls have no channel and are not shown on this tab.
                </p>
              )}
              {activeTab === 'ringGroups' && (
                <p className="u-text-muted u-text-small">
                  Ring groups only apply to inbound calls on the rhinoVoip phone system. RhinoCall calls have no ring group and are not shown on this tab.
                </p>
              )}
              {renderActiveTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RhinoVOIPReports;
